import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useConvexAuth } from 'convex/react';
import { useAuthActions } from '@convex-dev/auth/react';
import { api } from '@convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { InviteAuthForm } from '@/components/auth/InviteAuthForm';

const roleColors: Record<string, string> = {
  admin: '!bg-red-100 !text-red-800 dark:!bg-red-900/30 dark:!text-red-400',
  manager: '!bg-blue-100 !text-blue-800 dark:!bg-blue-900/30 dark:!text-blue-400',
  employee: '!bg-green-100 !text-green-800 dark:!bg-green-900/30 dark:!text-green-400',
  accountant: '!bg-purple-100 !text-purple-800 dark:!bg-purple-900/30 dark:!text-purple-400',
};

function CompanyInviteAuthFormWrapper({
  email,
  token,
  companyName,
  onAccepted,
}: {
  email: string;
  token: string;
  companyName: string;
  onAccepted: () => void;
}) {
  const emailExists = useQuery(api.companies.invitations.checkEmailExists, { email });

  if (emailExists === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 aria-hidden="true" className="h-8 w-8 animate-spin text-primary" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <InviteAuthForm
        email={email}
        token={token}
        orgName={companyName}
        initialMode={emailExists ? 'signIn' : 'signUp'}
        onAccepted={onAccepted}
      />
    </div>
  );
}

export function CompanyInviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useConvexAuth();
  const { signOut } = useAuthActions();

  const invitation = useQuery(api.companies.invitations.getByToken, token ? { token } : 'skip');
  const currentUser = useQuery(api.users.queries.getCurrentUser, isAuthenticated ? {} : 'skip');

  const acceptInvitation = useMutation(api.companies.invitations.acceptCompanyInvitation);
  const declineInvitation = useMutation(api.companies.invitations.declineCompanyInvitation);

  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  // Loading
  if (invitation === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 aria-hidden="true" className="h-8 w-8 animate-spin text-primary" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  // Invalid token
  if (!invitation || !token) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <XCircle aria-hidden="true" className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Invalid Invitation</h1>
          <p className="text-muted-foreground mb-6">
            This invitation link is invalid or has expired.
          </p>
          <Button onClick={() => navigate('/signin')}>Go to Sign In</Button>
        </div>
      </div>
    );
  }

  // Already used
  if (invitation.status !== 'pending') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <CheckCircle
            aria-hidden="true"
            className="h-12 w-12 text-muted-foreground mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Invitation {invitation.status}
          </h1>
          <p className="text-muted-foreground mb-6">
            This invitation has already been {invitation.status}.
          </p>
          <Button onClick={() => navigate(isAuthenticated ? '/company/employees' : '/signin')}>
            {isAuthenticated ? 'Go to Dashboard' : 'Go to Sign In'}
          </Button>
        </div>
      </div>
    );
  }

  // Expired
  if (invitation.expiresAt < Date.now()) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <XCircle aria-hidden="true" className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Invitation Expired</h1>
          <p className="text-muted-foreground mb-6">
            This invitation has expired. Ask the company admin to send a new one.
          </p>
          <Button onClick={() => navigate('/signin')}>Go to Sign In</Button>
        </div>
      </div>
    );
  }

  // Not authenticated — inline auth form
  if (!isAuthenticated) {
    return (
      <CompanyInviteAuthFormWrapper
        email={invitation.email}
        token={token}
        companyName={invitation.companyName}
        onAccepted={() => navigate('/company/employees', { replace: true })}
      />
    );
  }

  // Authenticated but still loading user data
  if (currentUser === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 aria-hidden="true" className="h-8 w-8 animate-spin text-primary" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  // Authenticated with wrong email — show mismatch warning
  const userEmail = currentUser?.email?.toLowerCase();
  const inviteEmail = invitation.email.toLowerCase();
  if (userEmail && userEmail !== inviteEmail) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8 bg-card rounded-lg border border-border">
          <div className="rounded-full bg-amber-100 p-3 w-fit mx-auto mb-4 dark:bg-amber-900/30">
            <AlertTriangle
              aria-hidden="true"
              className="h-6 w-6 text-amber-600 dark:text-amber-400"
            />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Wrong Account</h1>
          <p className="text-muted-foreground mb-2">
            You&apos;re signed in as <strong>{userEmail}</strong>, but this invitation was sent to:
          </p>
          <p className="text-foreground font-medium mb-4">{invitation.email}</p>
          <p className="text-sm text-muted-foreground mb-6">
            Please sign out and sign in with the correct email to accept this invitation.
          </p>
          <Button onClick={() => void signOut()}>Sign Out</Button>
        </div>
      </div>
    );
  }

  // Authenticated — accept/decline
  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      const result = await acceptInvitation({ token });
      if (result.success) {
        navigate('/company/employees', { replace: true });
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = async () => {
    setIsDeclining(true);
    try {
      await declineInvitation({ token });
      navigate('/company/employees', { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center max-w-md p-8 bg-card rounded-lg border border-border">
        <h1 className="text-2xl font-bold text-foreground mb-2">Company Invitation</h1>
        <p className="text-muted-foreground mb-1">
          You&apos;ve been invited to join <strong>{invitation.companyName}</strong>.
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          Accepting as <strong>{userEmail}</strong>
        </p>

        {/* Show assigned roles */}
        <div className="flex justify-center gap-1.5 mb-6">
          {invitation.roles.map((role: string) => (
            <Badge
              key={role}
              className={`!border-0 ${roleColors[role] ?? roleColors['employee']}`}
              variant="outline"
            >
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </Badge>
          ))}
        </div>

        <div className="flex gap-3 justify-center">
          <Button onClick={handleAccept} disabled={isAccepting || isDeclining}>
            {isAccepting ? (
              <>
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin mr-2" />
                Accepting...
              </>
            ) : (
              'Accept'
            )}
          </Button>
          <Button variant="outline" onClick={handleDecline} disabled={isAccepting || isDeclining}>
            {isDeclining ? (
              <>
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin mr-2" />
                Declining...
              </>
            ) : (
              'Decline'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
